import callApi from '../../util/apiCaller';
import { each, reduce, values, uniq, indexOf, filter, first, last, range } from 'lodash';
// Legacy constraints for reference:
// const constraints = {
//   first_name: { presence: true },
//   last_name: { presence: true },
//   selected_product_id: { presence: true },
//   phone_extension: { numericality: true },
//   email: { presence: true, email: true },
// };
// const accountConstraints = {
//   name: { presence: true },
//   annual_revenue: { numericality: { notLessThan: 0 } },
//   number_of_employees: { numericality: { notLessThan: 0 } },
//   website: { presence: { allowEmpty: true }, url: true },
// };
import Joi from 'joi';
import moment from 'moment';

export const CE_LOAD_ACCOUNTS = 'CE_LOAD_ACCOUNTS';
export const CE_RESET_SELECTED_ACCOUNTS = 'CE_RESET_SELECTED_ACCOUNTS';
export const CE_LOAD_TRACKINGS = 'CE_LOAD_TRACKINGS';
export const CE_SELECT_ACCOUNT = 'CE_SELECT_ACCOUNT';
export const CE_UNSELECT_ACCOUNT = 'CE_UNSELECT_ACCOUNT';
export const CE_TOGGLE_ALL_ACCOUNTS = 'CE_TOGGLE_ALL_ACCOUNTS';
export const CE_EDIT_ACCOUNT = 'CE_EDIT_ACCOUNT';
export const CE_UPDATE_ACCOUNT = 'CE_UPDATE_ACCOUNT';
export const CE_EDIT_ACCOUNT_SELECT_CONTACTS = 'CE_EDIT_ACCOUNT_SELECT_CONTACTS';
export const CE_EDIT_ACCOUNT_EDIT_CONTACT = 'CE_EDIT_ACCOUNT_EDIT_CONTACT';
export const CE_EDIT_ACCOUNT_UPDATE_CONTACT = 'CE_EDIT_ACCOUNT_UPDATE_CONTACT';
export const CE_UPDATE_AMOUNT_RANGE = 'CE_UPDATE_AMOUNT_RANGE';
export const CE_SUBMIT_SAMPLES = 'CE_SUBMIT_SAMPLES';
export const CE_LOAD_CONTACT_DISPOSITIONS = 'CE_LOAD_CONTACT_DISPOSITIONS';
export const CE_FETCH_UNLINKED_CONTACTS = 'CE_FETCH_UNLINKED_CONTACTS';

export const CE_FETCH_SAVED_PARAMS = 'CE_FETCH_SAVED_PARAMS';
export const CE_ADD_SAVED_PARAMS = 'CE_ADD_SAVED_PARAMS';
export const CE_REMOVE_SAVED_PARAMS = 'CE_REMOVE_SAVED_PARAMS';
export const CE_MASS_FETCH_OPPORTUNITY_CONTACTS = 'CE_MASS_FETCH_OPPORTUNITY_CONTACTS';

export const CE_FETCH_SUBMITTED_PARAMS = 'CE_FETCH_SUBMITTED_PARAMS';

export function validateContact(contact) {
  const schema = Joi.object({
    first_name: Joi.string().required().messages({
      'string.base': 'First name must be a string',
      'string.empty': 'First name is required',
      'any.required': 'First name is required',
    }),
    last_name: Joi.string().required().messages({
      'string.base': 'Last name must be a string',
      'string.empty': 'Last name is required',
      'any.required': 'Last name is required',
    }),
    selected_product_id: Joi.any().required().messages({
      'any.required': 'Product selection is required',
    }),
    phone_extension: Joi.number().allow('', null).messages({
      'number.base': 'Phone extension must be a number',
    }),
    email: Joi.string().email({ tlds: { allow: false } }).required().messages({
      'string.base': 'Email must be a string',
      'string.empty': 'Email is required',
      'any.required': 'Email is required',
      'string.email': 'Email must be a valid email address',
    }),
  }).unknown(true);
  const { error } = schema.validate(contact, { abortEarly: false });
  if (!error) return {};
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
}
export function validateAccount(account) {
  const schema = Joi.object({
    name: Joi.string().required().messages({
      'string.base': 'Name must be a string',
      'string.empty': 'Name is required',
      'any.required': 'Name is required',
    }),
    annual_revenue: Joi.number().min(0).allow('', null).messages({
      'number.base': 'Annual revenue must be a number',
      'number.min': 'Annual revenue must be at least 0',
    }),
    number_of_employees: Joi.number().min(0).allow('', null).messages({
      'number.base': 'Number of employees must be a number',
      'number.min': 'Number of employees must be at least 0',
    }),
    website: Joi.string().uri().allow('', null).messages({
      'string.base': 'Website must be a string',
      'string.uri': 'Website must be a valid URL',
    }),
  }).unknown(true);
  const { error } = schema.validate(account, { abortEarly: false });
  if (!error) return {};
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
}

export function resetAccounts() {
  return (dispatch) => {
    dispatch({
      type: CE_LOAD_ACCOUNTS, accounts: [], indexedContacts: {}, status: null, errors: {}, amounts: [],
    });
    return Promise.resolve();
  };
}

function makeAmountRange(input) {
  let amounts = uniq(input).sort((a, b) => (a - b));
  if (indexOf(amounts, 0) !== 0) {
    amounts.unshift(0); // start from 0
  }
  amounts = uniq((filter(amounts, a => (a >= 0 && a !== null))).sort((a, b) => (a - b)).map(a => Math.ceil(a)));
  amounts = uniq(filter(amounts.map((a) => {
    if (a >= 0) {
      return Math.ceil(a);
    }
    return null;
  }), a => (a != null))).sort((a, b) => (a - b));
  const length = amounts.length;
  if (length >= 100) {
    const min = first(amounts);
    const max = last(amounts);
    // we want to make 100  points
    const step = Math.floor((length - 2) / 98);
    amounts = filter(range(100).map((r) => {
      if (r > 0 && r < length) {
        return amounts[(r * step)];
      }
      return null;
    }), a => (a !== null));
    amounts.unshift(min);
    amounts.push(max);
  }
  return amounts;
}

export function fetchAccounts(criteria) {
  return (dispatch) => {
    dispatch({
      type: CE_LOAD_ACCOUNTS, accounts: [], indexedContacts: {}, status: 'loading', errors: null, amounts: [],
    });

    const { team } = criteria;
    const teamId = team && team._id ? `&teamId=${team._id}` : '';
    const p = callApi(`search/nominations?include=owners,accounts,lineItems,contactRoles,contacts${teamId}`, 'POST', criteria);
    p.then(
      (res) => {
        const {
          owners, opportunities, accounts, contacts,
        } = res;
        /*
        [
          {
            ...account,
            account_id,
            opportunities: [],
            contacts: [],
          }
        ]
        */
        const contactToAccount = {};
        const groupedOpportunities = reduce(opportunities, (results, opportunity) => {
          const account = accounts[opportunity.account_id];
          const closeDate = opportunity.close_date ? moment(opportunity.close_date) : null;

          if (!results[opportunity.account_id]) {
            if (account) {
              results[opportunity.account_id] = {
                ...account,
                owner: owners[account.owner_id] || null, // account owner, not opportunity owner!
                // opportunities: [],
                productIds: [], // all products across all opportunities
                opportunityContactIds: [], // all contacts across all opportunities
                indexedContacts: {},
                contacts: [], // all
                opportunityContacts: [],
                accountContacts: [],
                amount: 0,
                totalOpportunities: 0,
                opportunityIds: [],
                minCloseDate: closeDate,
              };
            }
          } else if (closeDate) {
            results[opportunity.account_id].minCloseDate = moment.min(results[opportunity.account_id].minCloseDate, closeDate);
          }

          if (results[opportunity.account_id]) {
            const newAccount = results[opportunity.account_id];
            newAccount.amount += opportunity.amount || 0;
            newAccount.totalOpportunities += 1;
            newAccount.opportunityIds.push(opportunity.opportunity_id);
            (opportunity.line_items || []).map((lineItem) => {
              newAccount.productIds.push(lineItem.product_id);
              return lineItem;
            });

            const roles = {};
            (opportunity.contact_roles || []).map((contactRole) => {
              newAccount.opportunityContactIds.push(contactRole.contact_id);
              roles[contactRole.contact_id] = contactRole.name; // there may be duplicated!!!
              return contactRole;
            });
            newAccount.submitted = false;
            newAccount.roles = roles;
            newAccount.productIds = uniq(newAccount.productIds);
            newAccount.opportunityContactIds = uniq(newAccount.opportunityContactIds);
            newAccount.indexedContacts = {};
            newAccount.contacts = [];
            each(newAccount.opportunityContactIds, (opportunityContactId) => {
              if (!contactToAccount[opportunityContactId]) {
                contactToAccount[opportunityContactId] = [];
              }
              contactToAccount[opportunityContactId].push(newAccount.account_id);
            });
            // console.log('contactscontacts', contacts.length);
            // each(contacts, contact => {
            // if (!newAccount.indexedContacts[contact.contact_id]) {
            // const isOpportunityContact = indexOf(newAccount.opportunityContactIds, contact.contact_id) >= 0;
            /*
                if (contact.account_id === account.account_id || isOpportunityContact) {
                  const newContact = {
                    ...contact,
                    isOpportunityContact,
                    role: roles[contact.contact_id],
                  };
                  newAccount.indexedContacts[contact.contact_id] = newContact;
                  newAccount.contacts.push(newContact);
                }
                */
            // }
            // });
          }
          return results;
        }, {});
        each(contacts, (contact) => {
          const account = contact.account_id ? groupedOpportunities[contact.account_id] : null;
          const isOpportunityContact = account && indexOf(account.opportunityContactIds, contact.contact_id) >= 0;
          const role = account ? account.roles[contact.contact_id] : null;
          const newContact = {
            ...contact,
            isOpportunityContact,
            role,
          };
          if (account) {
            account.indexedContacts[newContact.contact_id] = newContact;
            account.contacts.push(newContact);
            if (isOpportunityContact) {
              account.opportunityContacts.push(newContact);
            } else {
              account.accountContacts.push(newContact);
            }

          } else {
            const accountIds = uniq(contactToAccount[newContact.contact_id] || []);
            const opportunityContact = { ...newContact, isOpportunityContact: true };
            each(accountIds, (accountId) => {
              groupedOpportunities[accountId].indexedContacts[newContact.contact_id] = opportunityContact;
              groupedOpportunities[accountId].contacts.push(opportunityContact);
              groupedOpportunities[accountId].opportunityContacts.push(newContact);
            });
          }
        });

        const newAccounts = values(groupedOpportunities);
        let amounts = newAccounts.map(account => account.amount);
        amounts = makeAmountRange(amounts);
        return dispatch({
          type: CE_LOAD_ACCOUNTS, accounts: newAccounts, amounts, indexedContacts: contacts, status: 'loaded', errors: null,
        });
      },
      errors => dispatch({
        type: CE_LOAD_ACCOUNTS, accounts: [], amounts: [], indexedContacts: {}, status: 'error', errors,
      }),
    );
    return p;
  };
}

export function resetSelectedAccounts() {
  return (dispatch) => {
    dispatch({
      type: CE_RESET_SELECTED_ACCOUNTS,
    });
    return Promise.resolve();
  };
}

export function loadContactDispositions(clientId, opportunityId, contactId) {
  return (dispatch) => {
    dispatch({
      type: CE_LOAD_CONTACT_DISPOSITIONS, opportunityId, contactId, activities: [], status: 'loading', errors: null,
    });
    return callApi(`truvoice/clients/${clientId}/opportunities/${opportunityId}/contacts/${contactId}/activities`, 'GET').then(
      results => dispatch({
        type: CE_LOAD_CONTACT_DISPOSITIONS, activities: results.activities, opportunityId, contactId, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: CE_LOAD_CONTACT_DISPOSITIONS, activities: [], opportunityId, contactId, status: 'error', errors: error.data || error,
      }),
    );
  };
}


export function selectAccount(account, tvProductMappings) {
  return (dispatch) => {
    dispatch({
      type: CE_SELECT_ACCOUNT,
      account,
      tvProductMappings,
    });
    return Promise.resolve();
  };
}
export function unselectAccount(account) {
  return (dispatch) => {
    dispatch({
      type: CE_UNSELECT_ACCOUNT,
      account,
    });
    return Promise.resolve();
  };
}
export function editAccount(account) {
  return (dispatch) => {
    dispatch({
      type: CE_EDIT_ACCOUNT,
      account,
    });
    return Promise.resolve();
  };
}
export function updateAccount(account) {
  return (dispatch) => {
    dispatch({
      type: CE_UPDATE_ACCOUNT,
      account,
    });
    return Promise.resolve(account);
  };
}

export function resetClientTrackings() {
  return (dispatch) => {
    dispatch({
      type: CE_LOAD_TRACKINGS, trackings: {}, status: null, errors: null,
    });
    return Promise.resolve();
  };
}


export function toogleAllAccounts(savedOnly = false, tvProductMappings = {}) {
  return (dispatch) => {
    dispatch({
      type: CE_TOGGLE_ALL_ACCOUNTS,
      savedOnly,
      tvProductMappings,
    });
    return Promise.resolve();
  };
}


export function editAccountSelectContacts(contacts) {
  return dispatch => dispatch({
    type: CE_EDIT_ACCOUNT_SELECT_CONTACTS,
    contacts,
  });
}

export function editAccountEditContact(contact) {
  return dispatch => dispatch({
    type: CE_EDIT_ACCOUNT_EDIT_CONTACT,
    contact,
  });
}

export function editAccountUpdateContact(contact) {
  return dispatch => dispatch({
    type: CE_EDIT_ACCOUNT_UPDATE_CONTACT,
    contact,
  });
}

export function updateAmountRange(r) {
  return dispatch => dispatch({
    type: CE_UPDATE_AMOUNT_RANGE,
    range: r,
  });
}

export function fetchClientTrackings(clientId, initiativeId, accountIds = [], loadSavedParams = false) {
  return (dispatch) => {
    const promises = [];
    if (accountIds.length === 0) {
      dispatch({
        type: CE_LOAD_TRACKINGS, trackings: {}, status: 'loaded', errors: null,
      });
      if (loadSavedParams) {
        dispatch({
          type: CE_FETCH_SAVED_PARAMS, accounts: [], status: 'loaded', errors: null,
        });
      }
      dispatch({
        type: CE_FETCH_SUBMITTED_PARAMS, accounts: [], status: 'loaded', errors: null,
      });
      return Promise.resolve();
    }

    dispatch({
      type: CE_LOAD_TRACKINGS, trackings: {}, status: 'loading', errors: null,
    });
    const p1 = callApi(`truvoice/clients/${clientId}/initiatives/${initiativeId}/ce-trackings`, 'POST', { account_ids: accountIds });
    p1.then(
      () => {},
      error => dispatch({
        type: CE_LOAD_TRACKINGS, trackings: {}, status: 'error', errors: error,
      }),
    );
    promises.push(p1);

    if (loadSavedParams) {
      dispatch({
        type: CE_FETCH_SAVED_PARAMS, accounts: [], status: 'loading', errors: null,
      });
      const p2 = callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/accounts`, 'POST', { ids: accountIds });
      p2.then(
        () => {},
        error => dispatch({
          type: CE_FETCH_SAVED_PARAMS, accounts: [], status: 'error', errors: error,
        }),
      );
      promises.push(p2);
    } else {
      promises.push(Promise.resolve({ accounts: [] }));
    }


    dispatch({
      type: CE_FETCH_SUBMITTED_PARAMS, accounts: [], status: 'loading', errors: null,
    });
    const p3 = callApi(`submitted-nominations/clients/${clientId}/initiatives/${initiativeId}/accounts`, 'POST', { ids: accountIds });
    p3.then(
      () => {},
      error => dispatch({
        type: CE_FETCH_SUBMITTED_PARAMS, accounts: [], status: 'error', errors: error,
      }),
    );
    promises.push(p3);

    const allP = Promise.all(promises);

    allP.then(([trackings, result2, result3]) => {
      // because of race condition
      dispatch({
        type: CE_LOAD_TRACKINGS, trackings, status: 'loaded', errors: null,
      });
      dispatch({
        type: CE_FETCH_SAVED_PARAMS, accounts: result2.accounts, status: 'loaded', errors: null,
      });
      dispatch({
        type: CE_FETCH_SUBMITTED_PARAMS, accounts: result3.accounts, status: 'loaded', errors: null,
      });
    });

    return allP;
  };
}

export function submitSample(clientId, programId, params) {
  return (dispatch) => {
    const p = callApi(`truvoice/clients/${clientId}/initiatives/${programId}/ce-samples`, 'POST', params);
    p.then((results) => {
      const addedSample = results.added_sample;
      dispatch({ type: CE_SUBMIT_SAMPLES, addedSample });
    });
    return p;
  };
}


export function addSavedParams(clientId, initiativeId, accounts = []) {
  return (dispatch) => {
    return callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/accounts/add`, 'POST', { accounts }).then(
      (results) => {
        return dispatch({
          type: CE_ADD_SAVED_PARAMS, accounts: results.accounts, error: null,
        });
      },
      error => dispatch({
        type: CE_ADD_SAVED_PARAMS, accounts: [], error,
      }),
    );
  };
}

export function removeSavedParams(clientId, initiativeId, ids = []) {
  return (dispatch) => {
    return callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/accounts/remove`, 'POST', { ids }).then(
      () => {
        return dispatch({
          type: CE_REMOVE_SAVED_PARAMS, ids, error: null,
        });
      },
      error => dispatch({
        type: CE_REMOVE_SAVED_PARAMS, ids: [], error,
      }),
    );
  };
}

export function resetSavedParams() {
  return (dispatch) => {
    dispatch({
      type: CE_FETCH_SAVED_PARAMS, accounts: [], status: null, errors: null,
    });
    return Promise.resolve();
  };
}


export function fetchUnlinkedContacts(clientId, initiativeId) {
  return (dispatch) => {
    const promises = [];
    dispatch({
      type: CE_FETCH_UNLINKED_CONTACTS, contacts: [], status: 'loading', errors: null,
    });
    const p1 = callApi(`truvoice/clients/${clientId}/initiatives/${initiativeId}/unlinked-contacts`, 'GET');
    p1.then(
      ({ contacts }) => dispatch({
        type: CE_FETCH_UNLINKED_CONTACTS, contacts, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: CE_FETCH_UNLINKED_CONTACTS, contacts: [], status: 'error', errors: error,
      }),
    );
    promises.push(p1);
    return Promise.all(promises);
  };
}



export function massFetchOpportunityContacts(opportunityIds) {
  return (dispatch) => {
    dispatch({
      type: CE_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts: [], status: 'loading', errors: null,
    });
    return callApi('search/nominations/opportunities/contacts/', 'POST', { ids: opportunityIds }).then(
      ({ contacts }) => dispatch({
        type: CE_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: CE_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts: [], status: 'error', errors: error.data || error,
      }),
    );
  };
}